import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { Chessground } from '@lichess-org/chessground';
import type { Api } from '@lichess-org/chessground/api';
import type { DrawBrushes } from '@lichess-org/chessground/draw';
import { REVIEW_API, type Review } from '@chess/api-review';
import { BoardModel, START_FEN, type BoardView, type ShortlistReading } from '@chess/api-review/board';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { CONSTRAINTS_API, type ConstraintRegistry } from '@chess/api-constraints';
import { useConstraint, useConstraintOwner, useConstraintRegistry } from '@chess/api-constraints/react';
import { isBrowsing, positionAt } from '@chess/api-review';
import { boardHold } from './hold.ts';
import { playerClocks } from './clocks.ts';
import { annotate } from './arrows.ts';
import { groundConfig, shapesOf } from './ground.ts';
import '@lichess-org/chessground/assets/chessground.base.css';
import '@lichess-org/chessground/assets/chessground.brown.css';
import '@lichess-org/chessground/assets/chessground.cburnett.css';
import './board.css';
import { STORE_API, type Color, type GameRecord, type GameStore } from '@chess/api-model';
import { useSelect } from '@chess/api-review/react';
import '@chess/api-skeleton/skeleton.css';
import { PlayerLine } from './PlayerLine.tsx';

// The board: Chessground, the board Lichess uses (GPL-3.0-or-later). It
// shows the position the review is on and marks the move that led there;
// the reader may move a piece, which plays the game's next move or starts a
// line of their own through the review interface. At every ply it draws two
// arrows, as v1 does, always, unless an unsolved puzzle hides them: the move
// to play in green (the opening variation's own move while the game is on
// its line, else the engine's best), and the move played in the game in red
// when it is a blunder under the user's rules (the app's grade,
// `GameScan.grade`, as the scoresheet and the Blunders screen read it) and
// in a quiet grey otherwise, for either player; when the played move is the
// green one there is one green arrow. When the shortlist has searched the
// position deeper, its reading replaces the scan's. The rule is in
// `arrows.ts`.
//
// What the board shows and whether it takes a move (the lock, the
// constraints, the legal moves) is the `BoardModel`'s
// (`@chess/api-review/board`); this file hands each `BoardView` to
// Chessground through ground.ts and each drop back to the model.
// Above and below the board stand the two players, each at the edge of the
// board their pieces start from (names and ratings from the game's stored
// record) and,
// at the right end, the material that player has lost so far, which another
// plugin fills through the `review.player.material` slot (`{ color }`;
// review-material), then the time they had left on the clock at the position
// shown (clocks.ts, from the game's `clocks`); the board has no heading of
// its own. While an owner holds the
// board's constraints (a puzzle) the board wears the accent frame and the
// side to move reads "your move" (hold.ts).

// The arrows' colours come from core-theme's colors.css (`--viz-arrow-*`,
// which name the grade and muted colours), resolved when the board is
// created. A custom property reads back as its declared text
// ("var(--viz-grade-best)"), so each is resolved through a hidden probe's
// computed `color`, an rgb() Chessground accepts. The hex beside each is
// used only when the token is missing, as on a test page without the theme;
// it is the token's current value, so a missing theme draws the same colours.
function themeColor(token: string, fallback: string): string {
  if (!getComputedStyle(document.documentElement).getPropertyValue(token).trim()) return fallback;
  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style.color = `var(${token})`;
  document.body.appendChild(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  return color || fallback;
}

// Chessground's DrawBrushes type requires its four default keys; blue and
// yellow are declared only to satisfy it and are never drawn, so their
// colours stay plain values with no theme token.
function brushes(): DrawBrushes {
  return {
    blue: { key: 'blue', color: '#9184d9', opacity: 0.85, lineWidth: 10 },
    yellow: { key: 'yellow', color: '#d9c36a', opacity: 0.8, lineWidth: 10 },
    green: { key: 'green', color: themeColor('--viz-arrow-best', '#6fd3a2'), opacity: 0.85, lineWidth: 10 },
    red: { key: 'red', color: themeColor('--viz-arrow-blunder', '#c9405a'), opacity: 0.85, lineWidth: 10 },
    grey: { key: 'grey', color: themeColor('--viz-arrow-played', '#9397ab'), opacity: 0.7, lineWidth: 10 },
  };
}

const other = (c: Color): Color => (c === 'white' ? 'black' : 'white');

export function setup(app: PiletApi) {
  let review: Review | undefined;
  let store: GameStore | undefined;
  const ready = Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<GameStore>(STORE_API)]).then(([r, s]) => {
    review = r;
    store = s;
  });

  /** The two players' lines: the side the board is turned to at the bottom, the other at the top. */
  const usePlayers = () => {
    const [apis, setApis] = React.useState(!!review);
    React.useEffect(() => {
      if (!apis) void ready.then(() => setApis(true));
    }, [apis]);
    const gameId = useSelect(apis ? review : undefined, (s) => s.gameId);
    const bottom = useSelect(apis ? review : undefined, (s) => s.game?.sides?.orientation ?? 'white') ?? 'white';
    const [record, setRecord] = React.useState<GameRecord | undefined>();
    React.useEffect(() => {
      setRecord(undefined);
      if (!gameId || !store) return;
      let alive = true;
      void store
        .record(gameId)
        .then((r) => alive && setRecord(r))
        .catch(() => undefined);
      return () => {
        alive = false;
      };
    }, [gameId, apis]);
    const of = (c: Color) => (record ? record[c] : undefined);
    // The clocks at the position shown; the selector runs once per review state.
    const clocks = useSelect(apis ? review : undefined, (s) => playerClocks(s));
    const clockOf = (c: Color) => (clocks ? { clock: clocks[c], clockRunning: clocks.running === c } : {});
    // The material that player has lost, before the clock: declared here, filled by review-material.
    const materialOf = (c: Color) => <app.Extension name="review.player.material" params={{ color: c }} />;
    return { top: other(bottom), bottom, of, clockOf, materialOf };
  };

  /** The hold on the board: the owner on its scope and the Next move constraint (hold.ts). */
  const useHold = () => {
    const [apis, setApis] = React.useState(!!review);
    React.useEffect(() => {
      if (!apis) void ready.then(() => setApis(true));
    }, [apis]);
    const registry = useConstraintRegistry(app);
    const scope = useSelect(apis ? review : undefined, (s) => s.scope);
    const owner = useConstraintOwner(registry, scope);
    const nextMove = useConstraint(registry, scope, 'nextMove').allow;
    const fen = useSelect(apis ? review : undefined, (s) => positionAt(s));
    const browsing = useSelect(apis ? review : undefined, (s) => isBrowsing(s)) ?? false;
    return boardHold(!!owner, nextMove, fen, browsing);
  };

  /**
   * The puzzle's mark (2026-10-12): when a puzzle is solved, or a wrong move
   * played (review-puzzles says so with `review.puzzleSolved` and
   * `review.puzzleFailed`), the board shows it for a beat: the frame and the
   * move's squares turn green or red and a badge stands over the board ("✓
   * Solved", "✗ Not that move"), then fade. Long enough to be seen; the
   * puzzle's own pause before the play-out, and before the take-back, cover
   * it.
   */
  const MARK_MS = 1600;
  type Mark = 'solved' | 'failed';
  const useMark = () => {
    const [mark, setMark] = React.useState<Mark | undefined>(undefined);
    React.useEffect(() => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const show = (kind: Mark) => () => {
        setMark(kind);
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => setMark(undefined), MARK_MS);
      };
      const onSolved = show('solved');
      const onFailed = show('failed');
      app.on('review.puzzleSolved', onSolved);
      app.on('review.puzzleFailed', onFailed);
      return () => {
        app.off('review.puzzleSolved', onSolved);
        app.off('review.puzzleFailed', onFailed);
        if (timer) clearTimeout(timer);
      };
    }, []);
    return mark;
  };

  const Board: React.FC = () => {
    const players = usePlayers();
    const hold = useHold();
    const mark = useMark();
    const host = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      const el = host.current;
      if (!el) return;
      let model: BoardModel | undefined;
      const ground: Api = Chessground(el, {
        fen: START_FEN,
        orientation: 'white',
        viewOnly: false,
        coordinates: true,
        drawable: { enabled: false, visible: true, brushes: brushes() },
        // A piece was moved on the board: hand it to the model, which plays it
        // through the review or refuses it; refused, the board is put back.
        movable: {
          free: false,
          showDests: true,
          dests: new Map(),
          events: {
            after: (orig, dest) => {
              const outcome = model?.input({ kind: 'move', from: orig, to: dest });
              if (!outcome || outcome.kind === 'refused') show(model?.state(), true);
            },
          },
        },
        draggable: { enabled: true },
        selectable: { enabled: true },
        premovable: { enabled: false },
      });
      // Chessground sizes itself from its element; redraw when that changes.
      const observer = new ResizeObserver(() => ground.redrawAll());
      observer.observe(el);
      // Chessground keeps the board's place on screen from the first time it
      // measures it, and forgets it only on a scroll or a window resize. The
      // panels around the board load later and can move it without resizing
      // it (arriving from another screen, the board moved down two ranks), so
      // a click picked the wrong square. Measure again before every press;
      // capturing on the host runs before Chessground's own handlers.
      const remeasure = () => ground.state.dom.bounds.clear();
      el.addEventListener('mousedown', remeasure, { capture: true });
      el.addEventListener('touchstart', remeasure, { capture: true, passive: true });

      // Hand a view to Chessground, only what changed: the model emits only
      // on a change, and the arrows are set apart from the position. `force`
      // re-applies the position after a refused drop, which left the piece
      // where the reader put it while the view stayed the same.
      let shown: BoardView | undefined;
      const show = (view: BoardView | undefined, force = false) => {
        if (!view) return;
        if (force || view !== shown) ground.set(groundConfig(view));
        if (view.arrows !== shown?.arrows) ground.setAutoShapes(shapesOf(view));
        shown = view;
      };

      let alive = true;
      let stopModel: (() => void) | undefined;
      // The shortlist's deeper reading arrives as an event, so the board depends on no plugin.
      let shortlist: ShortlistReading = {};
      const onShortlist = (event: ShortlistReading) => {
        shortlist = event;
        model?.setShortlist(event);
      };
      app.on('review.shortlist', onShortlist);
      void Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<Chess>(CHESS_API), app.getApi<ConstraintRegistry>(CONSTRAINTS_API)]).then(([r, chess, constraints]) => {
        if (!alive) return;
        model = new BoardModel({ review: r, chess, constraints, annotate });
        model.setShortlist(shortlist);
        stopModel = model.subscribe((view) => show(view));
        show(model.state());
      });

      return () => {
        alive = false;
        app.off('review.shortlist', onShortlist);
        stopModel?.();
        model?.dispose();
        observer.disconnect();
        el.removeEventListener('mousedown', remeasure, { capture: true });
        el.removeEventListener('touchstart', remeasure, { capture: true });
        ground.destroy();
      };
    }, []);
    return (
      <section className={`panel review-board${hold.framed ? ' review-board-held' : ''}${mark ? ` review-board-${mark}` : ''}`} aria-label="Board">
        <div className="board-grid">
          <div className="board-player-top">
            <PlayerLine color={players.top} player={players.of(players.top)} badge={hold.yourMove === players.top ? 'your move' : undefined} material={players.materialOf(players.top)} {...players.clockOf(players.top)} />
          </div>
          {/* What stands beside the board on its left, as tall as the board: the score bar. */}
          <div className="board-side">
            <app.Extension name="review.boardSide" />
          </div>
          <div className="board-stage">
            <div className="board-host" ref={host} />
            {mark && (
              <div className={`board-mark-badge board-mark-${mark}`} role="status" aria-live="polite">
                <span className="board-mark-sign" aria-hidden="true">{mark === 'solved' ? '✓' : '✗'}</span> {mark === 'solved' ? 'Solved' : 'Not that move'}
              </div>
            )}
          </div>
          <div className="board-player-bottom">
            <PlayerLine color={players.bottom} player={players.of(players.bottom)} badge={hold.yourMove === players.bottom ? 'your move' : undefined} material={players.materialOf(players.bottom)} {...players.clockOf(players.bottom)} />
          </div>
        </div>
      </section>
    );
  };

  app.registerExtension('review.board', Board);
}
